import React, { useState, useRef, useEffect } from 'react';
import { onboardingQuestions, OnboardingQuestion } from './OnboardingChatConfig';
import { motion } from 'framer-motion';
import { streamingChat } from '../../utils/streamingChat';
import { updateUserProfile, getMentorPersona } from '../../utils/userProfile';
import { isVagueResponse } from '../../utils/responseValidation';
import { calculateTextAnimationDuration } from '../../config/ui';
import { storage } from '../../utils/storage';
import { persistenceService } from '../../utils/persistenceService';
import AnimatedText from './AnimatedText';
import ErrorBoundary from '../ErrorBoundary';

// Constants for consistent timing across the component
const TIMEOUTS = {
  BUTTON_SHOW_DELAY: 1000,
  MESSAGE_ANIMATION: 500,
  OPTION_ANIMATION: 150,
  FOCUS_DELAY: 100,
  STEP_TRANSITION: 300,
  RESPONSE_DELAY: 800
} as const;

const LIMITS = {
  INPUT_MAX_LENGTH: 500
} as const;

interface Answers {
  [key: string]: string;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}


const ChatbotUIOnboarding: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(() => {
    return storage.getOnboardingData({ currentStep: 0 }).currentStep || 0;
  });
  
  const [answers, setAnswers] = useState<Answers>(() => {
    return storage.getOnboardingData({ answers: {} }).answers || {};
  });
  
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [llmFollowUpAnswered, setLlmFollowUpAnswered] = useState(false);
  const [showContinuationButtons, setShowContinuationButtons] = useState(false);
  const [isStreamingResponse, setIsStreamingResponse] = useState(false);
  const [conversationMode, setConversationMode] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);



  // Varied vague response messages
  const getVagueResponseMessage = (): string => {
    const messages = [
      "I'd love to understand you better. Could you share a bit more about what's on your mind?",
      "That's a start! Can you tell me a little more about what you're thinking?",
      "I'm here to listen. What's really going on for you right now?",
      "Sometimes it's hard to put things into words. What would help me understand better?",
      "I can sense there's more there. What's one thing that's been on your mind lately?",
      "Let's go a bit deeper. What's something that's been weighing on you, even slightly?"
    ];
    return messages[Math.floor(Math.random() * messages.length)];
  };


  const [optionsVisible, setOptionsVisible] = useState<boolean[]>([]);
  const [showOptions, setShowOptions] = useState(false);

  const isComplete = currentStep >= onboardingQuestions.length;
  const currentQ: OnboardingQuestion | undefined = onboardingQuestions[currentStep];

  // Save progress to storage
  useEffect(() => {
    storage.setOnboardingData({ currentStep, answers });
  }, [currentStep, answers]);

  // Improved auto-scroll: scroll to make room for new questions while keeping context
  useEffect(() => {
    if (messagesEndRef.current) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        const scrollContainer = messagesEndRef.current?.closest('.overflow-y-auto');
        if (scrollContainer) {
          // Get the current scroll position and total height
          const scrollHeight = scrollContainer.scrollHeight;
          const clientHeight = scrollContainer.clientHeight;
          const currentScrollTop = scrollContainer.scrollTop;
          
          // Calculate optimal scroll position to show new content while keeping some context
          const newScrollTop = scrollHeight - clientHeight;
          
          // Only scroll if we're not already at the bottom or if there's new content
          if (currentScrollTop < newScrollTop - 50) {
            scrollContainer.scrollTo({
              top: newScrollTop,
              behavior: 'smooth'
            });
          }
        }
      });
    }
  }, [messages]);


  // When the message animation finishes, start animating the options
  useEffect(() => {
    if (!currentQ || currentQ.type !== 'options') return;
    // Calculate the total animation time for the message - must match text animation timing
    // Use the actual content that gets displayed (with placeholder replacements)
    let promptContent = currentQ.prompt;
    if (promptContent.includes('{name}')) {
      const userName = (answers['name'] || 'there').trim();
      promptContent = promptContent.replace('{name}', userName);
    }
    
    const totalMessageTime = calculateTextAnimationDuration(promptContent);
    setShowOptions(false);
    setOptionsVisible(Array(currentQ.options.length).fill(false));
    const timeout = setTimeout(() => {
      setShowOptions(true);
      // Animate each option in sequence
      currentQ.options.forEach((_, i) => {
        setTimeout(() => {
          setOptionsVisible(prev => {
            const next = [...prev];
            next[i] = true;
            return next;
          });
        }, i * TIMEOUTS.OPTION_ANIMATION);
      });
    }, totalMessageTime);
    return () => clearTimeout(timeout);
  }, [currentQ, answers]);

  // Note: Removed AI streaming integration as we now use predefined follow-ups

  // Initialize messages for current step
  useEffect(() => {
    if (!currentQ) return;

    if (currentQ.type === 'text' || currentQ.type === 'options') {
      // Add the question as an assistant message with placeholder replacement
      let promptContent = currentQ.prompt;
      // Replace {name} placeholder with user's entered name
      if (promptContent.includes('{name}')) {
        const userName = (answers['name'] || 'there').trim();
        promptContent = promptContent.replace('{name}', userName);
      }
      
      const questionMessage: ChatMessage = {
        id: `step-${currentStep}`,
        role: 'assistant',
        content: promptContent,
        timestamp: new Date()
      };
      setMessages([questionMessage]);
    } else if (currentQ.type === 'chat') {
      // For chat type, initialize if no messages or if this is a fresh step
      // Check if we have the intro message for this step
      const hasIntroMessage = messages.some(m => m.id === `step-${currentStep}-intro`);
      if (!hasIntroMessage) {
        let promptContent = currentQ.prompt;
        // Replace {priority} placeholder with user's selected priority
        if (promptContent.includes('{priority}')) {
          promptContent = promptContent.replace('{priority}', (answers['priority'] || 'top priority').toLowerCase());
        }
        
        const initialMessage: ChatMessage = {
          id: `step-${currentStep}-intro`,
          role: 'assistant',
          content: promptContent,
          timestamp: new Date()
        };
        setMessages([initialMessage]);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, currentQ, messages.length, answers]);

  // Focus the textarea input every time it appears and is enabled
  useEffect(() => {
    // Always focus when input should be shown, regardless of llmFollowUpAnswered state
    if (inputRef.current && shouldShowInlineInput()) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        setTimeout(() => {
          inputRef.current?.focus();
          // Ensure cursor is at the end of any existing text
          if (inputRef.current) {
            const length = inputRef.current.value.length;
            inputRef.current.setSelectionRange(length, length);
          }
        }, TIMEOUTS.STEP_TRANSITION);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [llmFollowUpAnswered, currentStep, messages, showContinuationButtons, conversationMode]);

  // Helper function to determine if inline input should be shown
  const shouldShowInlineInput = () => {
    // NEVER show input when streaming a response
    if (isStreamingResponse) {
      return false;
    }
    
    // In conversation mode, always show input when there are messages and not streaming
    if (conversationMode) {
      const lastMessage = messages[messages.length - 1];
      return lastMessage?.role === 'assistant' && !isStreamingResponse;
    }
    
    // For regular onboarding questions, show input for 'text' and 'chat' types only
    if (currentQ?.type !== 'chat' && currentQ?.type !== 'text') return false;
    
    const lastMessage = messages[messages.length - 1];
    
    // Show input if: last message is from assistant AND not currently streaming
    const shouldShow = lastMessage?.role === 'assistant' && !isStreamingResponse;
    
    return shouldShow;
  };

  const handleSendMessage = async () => {
    
    if (!inputValue.trim()) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    
    // Trigger smooth scroll to show new content
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 100);

    // Handle conversation mode (streaming responses)
    if (conversationMode) {
      setIsStreamingResponse(true);
      setShowContinuationButtons(false);
      
      try {
        // Build conversation history including the new user message
        const persona = getMentorPersona();
        const allMessages = [...messages, userMessage];
        const conversationHistory = allMessages.map(msg => ({
          role: msg.role as 'user' | 'assistant',
          content: msg.content
        }));

        // Add system message with persona context
        const systemMessage = {
          role: 'system' as const,
          content: `You are a ${persona === 'supportive' ? 'supportive and encouraging' : 'firm and focused'} mentor helping with momentum building. Continue the conversation naturally with relevant comments and follow-up questions to help them go deeper into their thoughts and goals.`
        };

        const aiMessages = [systemMessage, ...conversationHistory];

        await streamingChat(aiMessages, {
          forceOnboardingEndpoint: true,
          onPartialUpdate: (partial) => {
            setMessages(prev => {
              const lastMessage = prev[prev.length - 1];
              if (lastMessage?.role === 'assistant' && lastMessage.id === 'streaming-response') {
                return prev.map(msg => 
                  msg.id === 'streaming-response' 
                    ? { ...msg, content: partial }
                    : msg
                );
              } else {
                return [...prev, {
                  id: 'streaming-response',
                  role: 'assistant',
                  content: partial,
                  timestamp: new Date()
                }];
              }
            });
          },
          onComplete: (complete) => {
            setMessages(prev => 
              prev.map(msg => 
                msg.id === 'streaming-response' 
                  ? { ...msg, id: `response-${Date.now()}`, content: complete }
                  : msg
              )
            );
            setIsStreamingResponse(false);
            setIsLoading(false); // Set loading to false when streaming completes
            
            // Show continuation buttons after AI response in conversation mode
            setTimeout(() => {
              setShowContinuationButtons(true);
              // Focus input after AI response completes
              setTimeout(() => {
                inputRef.current?.focus();
              }, TIMEOUTS.FOCUS_DELAY);
            }, TIMEOUTS.BUTTON_SHOW_DELAY);
          },
          onError: (error) => {
            console.error('Streaming error:', error);
            setIsStreamingResponse(false);
            setMessages(prev => [...prev, {
              id: `error-${Date.now()}`,
              role: 'assistant',
              content: 'I apologize, but I encountered an error. Please try again.',
              timestamp: new Date()
            }]);
          }
        });
      } catch (error) {
        console.error('Conversation mode streaming error:', error);
        setIsStreamingResponse(false);
      }
      
      setIsLoading(false);
      return;
    }

    // Handle regular onboarding flow
    if (!currentQ) return;

    // Handle different question types
    if (currentQ.type === 'text') {
      // Check for vague response and provide guidance
      if (isVagueResponse(inputValue, currentQ.key)) {
        // Add a helpful AI message before continuing
        const helpMessage: ChatMessage = {
          id: `ai-help-${Date.now()}`,
          role: 'assistant',
          content: getVagueResponseMessage(),
          timestamp: new Date()
        };
        
        setMessages(prev => [...prev, helpMessage]);
        setIsLoading(false);
        return; // Don't advance to next step, let them try again
      }
      
      // Save answer and move to next step
      setAnswers(prev => ({ ...prev, [currentQ.key]: inputValue.trim() }));
      setCurrentStep(prev => prev + 1);
    } else if (currentQ.type === 'options') {
      // For options, we'll handle this differently with quick replies
      setAnswers(prev => ({ ...prev, [currentQ.key]: inputValue }));
      setCurrentStep(prev => prev + 1);
    } else if (currentQ.type === 'chat') {
      // Check for vague response first
      if (isVagueResponse(inputValue, currentQ.key)) {
        
        // Add a helpful AI message before continuing
        const helpMessage: ChatMessage = {
          id: `ai-help-${Date.now()}`,
          role: 'assistant',
          content: getVagueResponseMessage(),
          timestamp: new Date()
        };
        
        setMessages(prev => [...prev, helpMessage]);
        setIsLoading(false);
        
        // Trigger scroll to show help message
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }, 100);
        
        return; // Don't advance, let them try again
      }
      
      // For chat type, handle sequential questions with AI responses per spec
      const currentUserMessages = messages.filter(m => m.role === 'user').length + 1; // +1 for the message we just added
      const questionIndex = currentUserMessages - 1; // 0-based index (0 = first question, 1 = second question)
      
      if (questionIndex === 0) {
        // After first question: Add AI response (1 sentence) + second question in same bubble
        setTimeout(() => {
          const combinedMessage: ChatMessage = {
            id: `ai-response-and-question-2-${Date.now()}`,
            role: 'assistant',
            content: `That makes complete sense.

What's one small thing you could do for yourself today that no one's asking for?`,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, combinedMessage]);
        }, TIMEOUTS.RESPONSE_DELAY);
      } else if (questionIndex === 1) {
        // After second question: Add AI response (2 sentences) + third question
        setTimeout(() => {
          const aiResponse: ChatMessage = {
            id: `ai-response-and-question-3-${Date.now()}`,
            role: 'assistant',
            content: `That's a beautiful way to care for yourself.

These small acts build consistency.

What's something coming up, even tiny, that you actually feel good about?`,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, aiResponse]);
        }, TIMEOUTS.RESPONSE_DELAY);
      } else if (questionIndex === 2) {
        // After third question: Add AI response with persona-specific final prompt and show continuation buttons
        setTimeout(() => {
          const persona = getMentorPersona();
          const secondSentence = persona === 'supportive'
            ? "That spark is something worth nurturing."
            : "That's your momentum point - let's use it.";
            
          const finalPrompt = persona === 'supportive' 
            ? "That spark matters.\n\nWant to keep reflecting or gently wrap up for today?"
            : "That's clarity worth building on.\n\nPush a bit further or close your log and move forward?";
            
          const aiResponse: ChatMessage = {
            id: `ai-final-response-${Date.now()}`,
            role: 'assistant',
            content: `I love that you're looking forward to something. ${secondSentence}

${finalPrompt}`,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, aiResponse]);
          
          // Show continuation buttons after text animation completes
          setTimeout(() => {
            setLlmFollowUpAnswered(true);
            // Calculate animation duration and show buttons after text completes
            const textAnimationDuration = calculateTextAnimationDuration(aiResponse.content);
            setTimeout(() => {
              setShowContinuationButtons(true);
            }, textAnimationDuration);
          }, TIMEOUTS.MESSAGE_ANIMATION);
        }, TIMEOUTS.RESPONSE_DELAY);
      } else {
        // No more questions - just show continuation buttons
        setTimeout(() => {
          setLlmFollowUpAnswered(true);
          // Show buttons immediately since there's no new message
          setShowContinuationButtons(true);
        }, TIMEOUTS.RESPONSE_DELAY);
      }
    }

    setIsLoading(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Use Enter to send, Shift+Enter for new lines
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
    // Keep focus in the textarea when Tab is pressed
    if (e.key === 'Tab') {
      e.preventDefault();
      // Optionally insert tab character if needed
      // const target = e.target as HTMLTextAreaElement;
      // const start = target.selectionStart;
      // const end = target.selectionEnd;
      // const newValue = inputValue.substring(0, start) + '\t' + inputValue.substring(end);
      // setInputValue(newValue);
      // setTimeout(() => {
      //   target.selectionStart = target.selectionEnd = start + 1;
      // }, 0);
    }
  };

  const handleOptionSelect = (option: string) => {
    if (!currentQ || currentQ.type !== 'options') return;
    
    setAnswers(prev => ({ ...prev, [currentQ.key]: option }));
    // Clear messages when moving to next step to ensure clean initialization
    setMessages([]);
    setCurrentStep(prev => prev + 1);
    
    // Trigger scroll to show new content after state update
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 100);
  };

  const handleEmojiOptionClick = (option: string) => {
    if (!currentQ || currentQ.type !== 'chat') return;
    
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: option,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    
    // Trigger scroll after adding user message
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 100);
    
    // Add the next follow-up question
    const userMessages = messages.filter(m => m.role === 'user');
    const followUpIndex = userMessages.length; // No -1 because we haven't added the message yet
    const followUp = currentQ.followUps[followUpIndex];
    
    if (followUp) {
      let followUpContent = typeof followUp === 'string' ? followUp : followUp.prompt;
      if (followUpContent.includes('{priority}')) {
        followUpContent = followUpContent.replace('{priority}', (answers['priority'] || 'top priority').toLowerCase());
      }
      
      const followUpMessage: ChatMessage = {
        id: `followup-${followUpIndex}`,
        role: 'assistant',
        content: followUpContent,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, followUpMessage]);
      
      // Check if this is the last follow-up question
      const isLastFollowUp = followUpIndex >= currentQ.followUps.length - 1;
      
      if (isLastFollowUp) {
        // Calculate animation duration and show buttons after FINAL follow-up text completes
        const textAnimationDuration = calculateTextAnimationDuration(followUpContent);
        setTimeout(() => {
          setLlmFollowUpAnswered(true);
          setShowContinuationButtons(true);
        }, textAnimationDuration);
      }
    } else {
      // No more predefined follow-ups available - show continuation buttons
      setTimeout(() => {
        setLlmFollowUpAnswered(true);
        // Show buttons immediately since there's no new message
        setShowContinuationButtons(true);
      }, 500);
    }
  };

  // Removed handleLLMFollowUp as we now use predefined follow-ups

  // Removed handleContinue as we now navigate directly to dashboard

  const handlePushFurther = async () => {
    // Enter conversation mode
    setConversationMode(true);
    setLlmFollowUpAnswered(true); // Ensure this is set for button logic
    setIsStreamingResponse(true);
    setShowContinuationButtons(false);
    
    // Focus input after clicking button
    setTimeout(() => {
      inputRef.current?.focus();
    }, TIMEOUTS.FOCUS_DELAY);
    
    try {
      // Build context for AI from the conversation so far
      const persona = getMentorPersona();
      const conversationHistory = messages.map(msg => ({
        role: msg.role as 'user' | 'assistant',
        content: msg.content
      }));

      // Add a user message indicating the "push further" action
      const pushFurtherMessage = {
        role: 'user' as const,
        content: '[User clicked "Push Further" - they want to go deeper into this topic]'
      };

      // Add system message with specific "push further" instructions
      const systemMessage = {
        role: 'system' as const,
        content: `You are a ${persona === 'supportive' ? 'supportive and encouraging' : 'firm and focused'} mentor helping with momentum building. The user just clicked "Push Further" indicating they want to explore this topic more deeply. Ask a thoughtful follow-up question that helps them dive deeper into their motivations, challenges, or specific plans. Be more specific and probing than your previous response. Don't repeat what you just said.`
      };

      const aiMessages = [systemMessage, ...conversationHistory, pushFurtherMessage];

      await streamingChat(aiMessages, {
        forceOnboardingEndpoint: true,
        onPartialUpdate: (partial) => {
          setMessages(prev => {
            const lastMessage = prev[prev.length - 1];
            if (lastMessage?.role === 'assistant' && lastMessage.id === 'streaming-response') {
              // Update existing streaming message
              return prev.map(msg => 
                msg.id === 'streaming-response' 
                  ? { ...msg, content: partial }
                  : msg
              );
            } else {
              // Add new streaming message
              return [...prev, {
                id: 'streaming-response',
                role: 'assistant',
                content: partial,
                timestamp: new Date()
              }];
            }
          });
        },
        onComplete: (complete) => {
          setMessages(prev => 
            prev.map(msg => 
              msg.id === 'streaming-response' 
                ? { ...msg, id: `response-${Date.now()}`, content: complete }
                : msg
            )
          );
          setIsStreamingResponse(false);
          
          // Ensure conversation mode is active
          setConversationMode(true);
          
          // Show both buttons and input after AI finishes speaking
          setTimeout(() => {
            setShowContinuationButtons(true);
            // Focus input after AI response completes
            setTimeout(() => {
              inputRef.current?.focus();
            }, TIMEOUTS.FOCUS_DELAY);
          }, TIMEOUTS.BUTTON_SHOW_DELAY);
        },
        onError: (error) => {
          console.error('Streaming error:', error);
          setIsStreamingResponse(false);
          // Add error message
          setMessages(prev => [...prev, {
            id: `error-${Date.now()}`,
            role: 'assistant',
            content: 'I apologize, but I encountered an error. Please try again.',
            timestamp: new Date()
          }]);
        }
      });
    } catch (error) {
      console.error('Push further error:', error);
      setIsStreamingResponse(false);
    }
  };

  const handleFinishLog = async () => {
    try {
      // Mark onboarding as complete
      const profile = storage.getUserProfile({});
      storage.setUserProfile({
        ...profile,
        onboardingStatus: 'complete'
      });
      
      // Also mark the legacy flag for compatibility
      localStorage.setItem('thinkmateOnboarded', 'true');
      
      // Preserve access granted state to ensure API access continues working
      const accessGranted = localStorage.getItem('thinkmate_access_granted');
      if (!accessGranted) {
        localStorage.setItem('thinkmate_access_granted', 'true');
        localStorage.setItem('thinkmate_access_granted_at', Date.now().toString());
      }

      // Create journal entry from onboarding responses
      console.log('🎯 Creating onboarding entry...', { answers });
      
      try {
        const allResponses = Object.values(answers).filter(Boolean);
        const allQuestions = onboardingQuestions.map(q => q.text);
        
        console.log('📝 Onboarding data:', { allResponses, allQuestions });
        
        // Always create an entry, even if responses are limited
        const entryContent = allResponses.length > 0 
          ? allQuestions.map((q, i) => `Q: ${q}\nA: ${allResponses[i] || 'No response'}`).join('\n\n')
          : 'Completed ThinkMate onboarding process. Ready to start building momentum!';
        
        const wordCount = allResponses.length > 0 
          ? allResponses.join(' ').trim().split(/\s+/).length 
          : 12;
        
        const entry = await persistenceService.createEntry({
          date: new Date().toISOString().slice(0, 10),
          type: 'reflection',
          content: {
            title: 'Welcome to ThinkMate - Onboarding Complete!',
            body: entryContent,
            questions: allQuestions.length > 0 ? allQuestions : ['Welcome to ThinkMate'],
            responses: allResponses.length > 0 ? allResponses : ['Onboarding completed successfully']
          },
          metadata: {
            wordCount: wordCount,
            tags: ['onboarding', 'welcome']
          }
        });
        
        console.log('✅ Onboarding entry created successfully:', entry);
      } catch (error) {
        console.error('❌ Failed to create onboarding entry:', error);
        // Continue with navigation even if entry creation fails
      }
      
      // Signal dashboard to scroll to top after page load
      sessionStorage.setItem('scrollToTop', 'true');
      
      // Navigate to root - let App.tsx routing logic handle where to go
      window.location.href = '/';
    } catch (error) {
      console.error('Error saving onboarding entry:', error);
      // Still complete onboarding even if entry creation fails
      window.location.href = '/';
    }
  };

  // Render quick reply options for options type questions
  const renderQuickReplies = () => {
    if (!currentQ || currentQ.type !== 'options') return null;
    return (
      <motion.div
        className="flex flex-col items-stretch gap-4 mt-4"
        initial="hidden"
        animate="visible"
        variants={{
          visible: { transition: { staggerChildren: 0.8 } },
          hidden: {},
        }}
      >
        {currentQ.options.map((option, index) => (
          <motion.div
            key={option}
            variants={{
              hidden: { opacity: 0, y: 10 },
              visible: { opacity: 1, y: 0, transition: { duration: 1.2, ease: 'easeOut' } },
            }}
          >
            <button
              onClick={() => handleOptionSelect(option)}
              className="w-full px-4 py-2 text-lg font-normal transition-all duration-200 focus:outline-none"
              style={{
                backgroundColor: '#E5E5E5',
                color: '#111111',
                border: 'none',
                borderRadius: '24px',
                fontFamily: "'Inter', 'Satoshi', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                opacity: showOptions && optionsVisible[index] ? 1 : 0,
                transition: 'opacity 0.4s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#D1D5DB';
                e.currentTarget.style.filter = 'brightness(0.95)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#E5E5E5';
                e.currentTarget.style.filter = 'brightness(1)';
              }}
            >
              {option}
            </button>
          </motion.div>
        ))}
      </motion.div>
    );
  };

  // Render completion summary
  if (isComplete) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center z-50" style={{
        background: `
          radial-gradient(circle at 20% 20%, rgba(255, 243, 212, 0.3) 0%, transparent 50%),
          radial-gradient(circle at 80% 80%, rgba(243, 240, 255, 0.3) 0%, transparent 50%),
          #FAFAFA
        `,
        fontFamily: "'Inter', 'Satoshi', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
      }}>
        <div className="w-full max-w-2xl h-[100vh] flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4 bg-white">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Welcome to ThinkMate! 🚀</h1>
                <p className="text-gray-600">You're all set up and ready to build momentum.</p>
              </div>
              
              <div className="space-y-4">
                {onboardingQuestions.map((q) => (
                  <div key={q.key} className="bg-gray-50 rounded-lg p-4 border">
                    <div className="text-sm text-gray-500 mb-1">{q.prompt}</div>
                    <div className="font-semibold text-gray-900">{answers[q.key]}</div>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 text-center">
                <button
                  onClick={async () => {
                    try {
                      // Save all onboarding answers to user profile before completion
                      const profileUpdates = {
                        onboardingStatus: 'complete' as const,
                        name: answers.name,
                        coachingStyle: answers.style === 'Firm and focused with direct feedback' ? 'noExcuses' as const : 'supportive' as const,
                        accountabilityStyle: answers.style === 'Firm and focused with direct feedback' ? 'noExcuses' : 'supportive',
                        priority: answers.priority,
                        blocker: answers.blocker,
                        support: answers.support,
                        checkinTime: answers.checkinTime,
                        onboardingCompletedAt: new Date().toISOString()
                      };
                      
                      const updateSuccess = await updateUserProfile(profileUpdates);
                      if (!updateSuccess) {
                        console.error('Failed to save onboarding data to server');
                        alert('Failed to save your onboarding data. Please try again or contact support.');
                        return;
                      }
                      
                      // Preserve access granted state to ensure API access continues working
                      const accessGranted = localStorage.getItem('thinkmate_access_granted');
                      if (!accessGranted) {
                        localStorage.setItem('thinkmate_access_granted', 'true');
                        localStorage.setItem('thinkmate_access_granted_at', Date.now().toString());
                      }
                      
                      // Create journal entry from onboarding responses
                      console.log('🎯 Creating onboarding entry (magic link path)...', { answers });
                      
                      try {
                        const allResponses = Object.values(answers).filter(Boolean);
                        const allQuestions = onboardingQuestions.map(q => q.text);
                        
                        console.log('📝 Onboarding data:', { allResponses, allQuestions });
                        
                        // Always create an entry, even if responses are limited
                        const entryContent = allResponses.length > 0 
                          ? allQuestions.map((q, i) => `Q: ${q}\nA: ${allResponses[i] || 'No response'}`).join('\n\n')
                          : 'Completed ThinkMate onboarding process. Ready to start building momentum!';
                        
                        const wordCount = allResponses.length > 0 
                          ? allResponses.join(' ').trim().split(/\s+/).length 
                          : 12;
                        
                        const entry = await persistenceService.createEntry({
                          date: new Date().toISOString().slice(0, 10),
                          type: 'reflection',
                          content: {
                            title: 'Welcome to ThinkMate - Onboarding Complete!',
                            body: entryContent,
                            questions: allQuestions.length > 0 ? allQuestions : ['Welcome to ThinkMate'],
                            responses: allResponses.length > 0 ? allResponses : ['Onboarding completed successfully']
                          },
                          metadata: {
                            wordCount: wordCount,
                            tags: ['onboarding', 'welcome']
                          }
                        });
                        
                        console.log('✅ Onboarding entry created successfully:', entry);
                      } catch (error) {
                        console.error('❌ Failed to create onboarding entry:', error);
                        // Continue with navigation even if entry creation fails
                      }
                      
                      localStorage.removeItem('onboardingAnswers');
                      window.location.href = '/check-in/day-1';
                    } catch (error) {
                      console.error('Failed to complete onboarding:', error);
                      alert('Failed to complete onboarding. Please try again or contact support.');
                    }
                  }}
                  className="px-6 py-3 text-white font-semibold transition-all duration-200"
                  style={{
                    backgroundColor: '#111111',
                    borderRadius: '24px',
                    border: 'none',
                    fontFamily: "'Inter', 'Satoshi', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  Start Your Journey
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="fixed inset-0" style={{
        background: `
          radial-gradient(circle at 20% 20%, rgba(255, 243, 212, 0.3) 0%, transparent 50%),
          radial-gradient(circle at 80% 80%, rgba(243, 240, 255, 0.3) 0%, transparent 50%),
          #FAFAFA
        `,
        fontFamily: "'Inter', 'Satoshi', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
      }} className="flex flex-col items-center justify-center z-50">
      <div className="w-full max-w-2xl h-[100vh] flex flex-col overflow-hidden">
        {/* Progress bar */}
        <div className="w-full h-1 bg-gray-200">
          <div
            className="h-1 transition-all duration-300"
            style={{ 
              backgroundColor: '#111111',
              width: `${(currentStep / onboardingQuestions.length) * 100}%`
            }}
          />
        </div>

        {/* Temporary Reset Button for Testing */}
        <div className="absolute bottom-4 right-4 z-10">
          <button
            onClick={() => {
              // Clear only onboarding-related localStorage
              localStorage.removeItem('onboardingAnswers');
              localStorage.removeItem('onboardingCurrentStep');
              
              // Reset all state to initial values
              setCurrentStep(0);
              setAnswers({});
              setMessages([]);
              setInputValue('');
              setLlmFollowUpAnswered(false);
              setShowContinuationButtons(false);
              setIsStreamingResponse(false);
              setConversationMode(false);
              setOptionsVisible([]);
              setShowOptions(false);
            }}
            className="px-2 py-1 text-xs transition-colors duration-200"
            style={{
              backgroundColor: '#FFF3D4',
              color: '#92400E',
              border: '1px solid #FDE68A',
              borderRadius: '6px',
              fontFamily: "'Inter', 'Satoshi', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#FEF3C7';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#FFF3D4';
            }}
            title="Reset onboarding for testing"
          >
            🚧 Reset
          </button>
        </div>

        {/* Chat area */}
        <div 
          className="flex-1 overflow-y-auto bg-white"
          onClick={(e) => {
            // Only focus input if not selecting text
            const selection = window.getSelection();
            if (selection && selection.toString().length === 0) {
              // Check if click target is not the text content itself
              const target = e.target as HTMLElement;
              if (!target.closest('.message-content') && shouldShowInlineInput() && inputRef.current) {
                inputRef.current.focus();
              }
            }
          }}
        >
          <div className="max-w-3xl mx-auto p-4 pb-20">
            {messages.map((message, index) => {
              if (message.role === 'assistant') {
                const isLastAssistantMessage = index === messages.length - 1;
                const showInlineInput = isLastAssistantMessage && shouldShowInlineInput();
                
                return (
                  <div key={message.id}>
                    <div className="mb-6 w-full">
                      <div className="mb-4 message-content" style={{ userSelect: 'text' }}>
                        <AnimatedText 
                          content={message.content}
                          delay={200}
                          className="leading-relaxed"
                          style={{ color: '#111111' }}
                        />
                      </div>
                      
                      {showInlineInput && (!showContinuationButtons || conversationMode) && (
                        <div className="mt-4">
                          <textarea
                            ref={inputRef}
                            value={inputValue}
                            onChange={(e) => {
                              const truncatedValue = e.target.value.slice(0, LIMITS.INPUT_MAX_LENGTH);
                              setInputValue(truncatedValue);
                            }}
                            onKeyDown={handleKeyDown}
                            placeholder="Write..."
                            className="w-full min-h-[120px] py-4 pl-0 pr-4 border-none bg-transparent focus:outline-none focus:ring-0 resize-none font-normal leading-relaxed"
                            style={{ 
                              color: '#111111', 
                              fontFamily: "'Inter', 'Satoshi', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                              resize: 'none',
                              minHeight: '120px',
                              height: 'auto'
                            }}
                            rows={6}
                            autoFocus
                            onInput={(e) => {
                              const target = e.target as HTMLTextAreaElement;
                              target.style.height = 'auto';
                              target.style.height = `${Math.max(120, target.scrollHeight)}px`;
                            }}
                          />
                        </div>
                      )}
                    </div>
                    
                    {showInlineInput && !conversationMode && !showContinuationButtons && inputValue.trim() && (
                      <div className="mb-6">
                        <button
                          onClick={handleSendMessage}
                          disabled={!inputValue.trim() || isLoading || isStreamingResponse}
                          className="disabled:cursor-not-allowed text-white font-semibold px-8 py-3 transition-all duration-200"
                          style={{
                            backgroundColor: inputValue.trim() ? '#111111' : '#E5E5E5',
                            color: inputValue.trim() ? '#FFFFFF' : '#111111',
                            borderRadius: '24px',
                            border: 'none'
                          }}
                          onMouseEnter={(e) => {
                            if (inputValue.trim()) {
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.boxShadow = 'none';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          {currentQ?.type === 'text' ? 'Continue' : 'Continue'}
                        </button>
                      </div>
                    )}
                  </div>
                );
              }
              // For user messages - show as response blocks
              if (message.role === 'user') {
                const lines = message.content.split(/\r?\n/);
                return (
                  <div
                    key={message.id}
                    className="mb-4 w-full"
                  >
                    <div className="message-content" style={{ userSelect: 'text', color: '#111111' }}>
                      <div className="whitespace-pre-wrap">
                        {lines.map((line, i) => (
                          <div key={i}>{line}</div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <div
                  key={message.id}
                  className={`mb-4 flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg px-4 py-2 ${
                      message.role === 'user'
                        ? 'bg-rose-600 text-white'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    <div className="whitespace-pre-wrap">{message.content}</div>
                  </div>
                </div>
              );
            })}
            
            {/* Scroll anchor for auto-scroll functionality */}
            <div ref={messagesEndRef} />
            
            {renderQuickReplies()}
            
            {/* Render emoji options for chat type follow-ups */}
            {currentQ?.type === 'chat' && (() => {
              const userMessages = messages.filter(m => m.role === 'user');
              const followUpIndex = userMessages.length;
              const followUp = currentQ.followUps[followUpIndex];
              
              if (followUp && typeof followUp !== 'string' && followUp.type === 'options') {
                return (
                  <div className="flex justify-center gap-2 mt-4">
                    {followUp.options.map((emoji, i) => (
                      <button
                        key={emoji}
                        className="text-3xl px-3 py-2 rounded-full border-2 transition-all duration-200 focus:outline-none"
                        style={{
                          borderColor: '#E5E5E5',
                          backgroundColor: 'transparent'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#111111';
                          e.currentTarget.style.backgroundColor = '#F3F0FF';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#E5E5E5';
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        onClick={() => handleEmojiOptionClick(emoji)}
                        aria-label={`Option ${i + 1}`}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                );
              }
              return null;
            })()}
            
            {/* Removed old AI streaming UI components */}
            
            {showContinuationButtons && (() => {
              const isFirmPersona = answers.style === 'Firm and focused with direct feedback';
              
              
              // Show persona-specific continuation buttons with animation
              return (
                <motion.div
                  className="flex gap-4 mt-4 mb-6 pb-4"
                  initial="hidden"
                  animate="visible"
                  variants={{
                    visible: { transition: { staggerChildren: 0.15 } },
                    hidden: {},
                  }}
                >
                  <motion.button
                    onClick={handlePushFurther}
                    disabled={isStreamingResponse}
                    className="px-8 py-3 text-white font-semibold transition-all duration-200 disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: isStreamingResponse ? '#6B7280' : '#111111',
                      borderRadius: '24px',
                      border: 'none',
                      fontFamily: "'Inter', 'Satoshi', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                    }}
                    onMouseEnter={(e) => {
                      if (!isStreamingResponse) {
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                    variants={{
                      hidden: { opacity: 0, y: 10 },
                      visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } },
                    }}
                  >
                    {isStreamingResponse ? 'Thinking...' : (isFirmPersona ? 'Push Further' : 'Keep Reflecting')}
                  </motion.button>
                  <motion.button
                    onClick={handleFinishLog}
                    disabled={isStreamingResponse}
                    className="px-8 py-3 text-white font-semibold transition-all duration-200 ml-auto disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: isStreamingResponse ? '#6B7280' : '#6B7280',
                      borderRadius: '6px',
                      border: 'none',
                      fontFamily: "'Inter', 'Satoshi', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                    }}
                    onMouseEnter={(e) => {
                      if (!isStreamingResponse) {
                        e.currentTarget.style.backgroundColor = '#4B5563';
                        e.currentTarget.style.filter = 'brightness(0.95)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isStreamingResponse) {
                        e.currentTarget.style.backgroundColor = '#6B7280';
                        e.currentTarget.style.filter = 'brightness(1)';
                      }
                    }}
                    variants={{
                      hidden: { opacity: 0, y: 10 },
                      visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } },
                    }}
                  >
                    Finish Log
                  </motion.button>
                </motion.div>
              );
            })()}
            
            {isLoading && (
              <div className="flex justify-start mb-4">
                <div className="bg-gray-100 rounded-lg px-4 py-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        </div>


      </div>
      </div>
    </ErrorBoundary>
  );
};

export default ChatbotUIOnboarding; 